import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronDown } from 'lucide-react';
import content from '@/data/homepageContent.json';
import { useAuth } from '@/lib/auth';
import { SITE_NAME, brand } from '@/lib/siteConfig';
import { ONE_TIME_PRICE_LABEL, oneTimeOffered } from '@/lib/calcUnlock';
import { PRO_MONTHLY_PRICE } from '@/constants/proTerms';
import PricingCards from '@/components/pricing/PricingCards';
import { PRO_TERMS } from '@/constants/proTerms';
import { STRIPE_PRIMARY, stripeDefaultCurrency } from '@/lib/visitorRegion';
import { useStripeProPrices } from '@/hooks/useStripeProPrices';
import { foreignProTerms, proPayButtonLabel } from '@/lib/foreignProTerms';
// Блок об авторе и классы кнопок живут отдельным модулем — его же
// импортирует /consultation, чтобы не тянуть в свой чанк всю главную.
import { CTA_BTN_CLASS, TINT_BG_CLASS } from './ExpertBlock';
import PaymentSystemsLine from '@/components/payment/PaymentSystemsLine';

// Реэкспорт: страницы и блоки, которые брали это из HomeSections
// (NewLandingPage, CategoryPage, CategoriesBlock), менять не пришлось.
export { ExpertBlock } from './ExpertBlock';
export { CTA_BTN_CLASS, TINT_BG_CLASS };

// ═══════════════════════════════════════════════════════════════════════
//  Pricing Block — та же витрина, что на /pricing (components/pricing/
//  PricingCards): Бесплатно · Разовая · ПРО · Личная консультация.
//  Кнопка ПРО здесь ведёт на /checkout выбранного срока, как и раньше.
// ═══════════════════════════════════════════════════════════════════════
export function PricingBlock() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  // Иностранцу на ру — цены ПРО и кнопка «Оформить ПРО — …» в его валюте,
  // те же правила и источник, что на /pricing (lib/foreignProTerms.js).
  // Запрос только у вошедшего иностранца на ру: гостю блока нет вовсе.
  const foreign = STRIPE_PRIMARY && isAuthenticated;
  const proPrices = useStripeProPrices(foreign, foreign ? stripeDefaultCurrency() : null);
  const proTerms = foreignProTerms(PRO_TERMS, proPrices);
  // Гостю витрины тарифов в ленте главной нет вовсе (решение владельца): на
  // его пути рекламы не должно быть, первое предложение ПРО человек видит в
  // кабинете. Консультация остаётся доступной из меню и на своей странице.
  // Выход ПОСЛЕ всех хуков (react-hooks/rules-of-hooks).
  if (!isAuthenticated) return null;
  return (
    <section className="bg-white py-2 md:py-3 px-4" data-testid="pricing-block">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-base md:text-lg font-heading font-bold text-center text-slate-900 mb-2">
          Выбери свой уровень погружения!
        </h2>
        <PricingCards
          compact
          place="home"
          availableTerms={proTerms}
          renderProActions={(term) => (
            <button
              type="button"
              onClick={() => navigate(term.path)}
              className="w-full py-2 rounded-lg bg-amber-600 text-white text-xs font-bold hover:bg-amber-700"
              data-testid="pricing-cta-pro"
            >
              {proPayButtonLabel(term.price)}
            </button>
          )}
        />
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  Регистрация на акцентном градиентном фоне (только гостю). Таблица
//  сравнения «Почему именно мы?» снята (4.2) — вместо неё WhyAstrofilBlock
//  «Зачем мне АСТРОфил?» выше блока цен.
// ═══════════════════════════════════════════════════════════════════════
export function RegisterWhyBlock() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  return (
    <>
      {/* Регистрация — акцентный блок. Показываем ТОЛЬКО гостям: залогиненному
          пользователю (он уже зарегистрирован) призыв «Зарегистрируйся» бьёт по
          доверию, особенно когда он пришёл по письму и видит своё имя в шапке. */}
      {!isAuthenticated && (
        <section
          className="bg-gradient-to-r from-accent-cta-500 to-accent-cta-700 py-4 md:py-6 px-4"
          data-testid="register-benefits-block"
        >
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-lg md:text-2xl font-heading font-extrabold text-white mb-3 md:mb-4">
              Зарегистрируйся, и получи уже сейчас
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mb-4 md:mb-5">
              {content.registerBenefits.map((b, i) => (
                <div
                  key={i}
                  className="bg-white/15 border border-white/30 rounded-lg px-3 py-2.5 text-white font-medium text-xs md:text-sm leading-snug"
                >
                  {b}
                </div>
              ))}
            </div>
            <Button
              size="lg"
              className="bg-white text-accent-cta-600 hover:bg-sky-50 px-8 py-3 h-auto text-base font-bold rounded-lg shadow-md whitespace-normal max-w-full"
              onClick={() => navigate('/auth/register')}
              data-testid="register-benefits-cta"
            >
              Зарегистрироваться · 14 дней бесплатно
            </Button>
          </div>
        </section>
      )}

    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  «Зачем мне АСТРОфил?» (4.2, переделан в 4.6; тексты владелицы — дословно).
//  Шесть плашек-вопросов: на ПК два ровных ряда по три одинаковой высоты,
//  видны только вопросы, ответ раскрывается под плашкой по нажатию, открыт
//  один. Телефон — столбиком, все свёрнуты. Название сайта и цены — по
//  сайту: brand()/SITE_NAME (рф — АСТРОфил, ру — ASTROfeel), разовая цена —
//  ONE_TIME_PRICE_LABEL (см. payAnswer), ПРО — PRO_MONTHLY_PRICE.
// ═══════════════════════════════════════════════════════════════════════
// Ответ «Сначала бесплатно, а потом придётся платить?»: разовая цена — та же,
// что на кнопке «Открыть полностью» (иностранцу на ру — в его валюте);
// россиянину на ру разовой покупки нет — только ПРО. Цена ПРО за месяц
// иностранцу на ру — из /api/stripe/pro-prices (proMonthLabel, см.
// WhyAstrofilBlock), как в блоке цен.
export function payAnswer(offered = oneTimeOffered(), priceLabel = ONE_TIME_PRICE_LABEL,
  proMonthLabel = `${PRO_MONTHLY_PRICE} ₽`) {
  const pro = `все 15+ программ за ${proMonthLabel} в месяц`;
  return 'Карта, первый ответ на вопрос и часть каждой программы открыты сразу. Регистрация в один клик, банковская карта не нужна. Платишь, только если захочешь больше: '
    + (offered ? `один разбор за ${priceLabel} или ${pro}.` : `${pro}.`);
}

export const WHY_CARDS = [
  {
    key: 'chatgpt',
    title: 'А почему не чат GPT?',
    text: brand('Спроси — и сравни. Чат пишет гладко, но карту он не считает: Асцендент, дома и положения планет называет по памяти и нередко ошибается, а завтра на тот же вопрос ответит иначе. АСТРОфил считает твою карту с астрономической точностью — по дате, времени и городу. Выводы не сочиняются на ходу: они строятся по правилам практикующего астролога. Одна карта — один разбор, с датами и часами.'),
    action: 'natal',
    actionLabel: 'Построить карту и сравнить',
  },
  {
    key: 'astrologer',
    title: 'А почему не обратиться к астрологу?',
    text: brand('Для большого разбора жизни — да, и заказать его можно здесь же. Но астролог не будет рядом каждое утро. АСТРОфил — это методика астролога Светланы Филатовой на каждый день: твой день, месяц, отношения, деньги. Лично твои разборы в любое время, когда тебе нужно.'),
    link: '/consultation',
    linkLabel: 'Личный видеоразбор',
  },
  {
    key: 'horoscope',
    title: 'Везде полно бесплатных гороскопов!',
    text: brand('Гороскоп пишут на знак — один текст на миллионы людей. АСТРОфил всё считает только по твоей карте, и под каждым выводом видно, откуда он взялся: какая планета, какой аспект. Проверь на себе.'),
  },
  {
    key: 'pay',
    title: 'Сначала бесплатно, а потом придётся платить?',
    text: payAnswer(),
  },
  {
    key: 'birth_time',
    title: 'Я не знаю время рождения!',
    text: 'И не нужно. Без времени рождения карта тоже считается. Но если время есть хоть примерно, лучше указать — так будет точнее. Разбираться в астрологии тоже не придётся: ответы простыми словами — что происходит, почему и что делать. Без запугивания.',
  },
  {
    key: 'learn',
    title: 'Я могу самостоятельно выучить астрологию!',
    text: brand('Можешь — но это годы: сотни правил, расчёты, практика на живых картах. А ответ нужен сейчас. АСТРОфил уже знает эти правила и считает твою карту за минуту. Захочешь разобраться сама — начни со своей карты: на ней учиться интереснее всего.'),
  },
];

/** Прокрутка к форме натальной карты наверху главной. */
function scrollToNatalForm() {
  const el = document.querySelector('[data-testid="cta-natal-card"]') || document.getElementById('natal-form');
  if (!el) return;
  if (el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  const input = el.querySelector('input, select');
  if (input && input.focus) setTimeout(() => input.focus({ preventScroll: true }), 400);
}

export function WhyAstrofilBlock() {
  // Открыт один ответ; при загрузке все свёрнуты (и на ПК, и на телефоне).
  const [openKey, setOpenKey] = useState(null);
  // Иностранцу на ру цена ПРО за месяц в ответе «…придётся платить?» — в его
  // валюте, тот же источник и те же правила, что у блока цен (PricingBlock,
  // lib/foreignProTerms.js): грузится — «…», не удалось — рубли. Запрос
  // только у иностранца на ру; рф и россиянину — без изменений.
  const proPrices = useStripeProPrices(STRIPE_PRIMARY, STRIPE_PRIMARY ? stripeDefaultCurrency() : null);
  const proMonth = foreignProTerms(PRO_TERMS, proPrices).find((t) => t.key === 'month');
  const payText = proPrices.status === 'loading' || proPrices.status === 'ready'
    ? payAnswer(oneTimeOffered(), ONE_TIME_PRICE_LABEL, proMonth ? proMonth.price : undefined)
    : null;
  const toggle = (key) => setOpenKey((prev) => (prev === key ? null : key));
  return (
    <section className="bg-accent-cta-75 py-3 md:py-4 px-4" data-testid="why-astrofil-block">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-base md:text-lg font-heading font-bold text-center text-slate-900 mb-3">
          Зачем мне {SITE_NAME}?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 items-start gap-2.5 md:gap-3">
          {WHY_CARDS.map((c) => {
            const isOpen = openKey === c.key;
            return (
              <div key={c.key} data-testid={`why-card-${c.key}`}>
                <button type="button" onClick={() => toggle(c.key)} aria-expanded={isOpen}
                  className={`w-full md:h-[4.5rem] flex items-center justify-between gap-2 text-left rounded-xl border px-3 py-3 md:px-4 shadow-sm transition-colors ${isOpen ? 'border-accent-cta-300 bg-accent-cta-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                  data-testid={`why-card-${c.key}-title`}>
                  <h3 className="text-base md:text-[17px] font-heading font-bold leading-snug text-slate-900">{c.title}</h3>
                  <ChevronDown className={`w-5 h-5 flex-shrink-0 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && (
                  <div className="mt-2 rounded-xl border border-slate-100 bg-slate-50/60 p-3 md:p-4" data-testid={`why-card-${c.key}-body`}>
                    <p className="text-sm leading-relaxed text-slate-700">{(c.key === 'pay' && payText) || c.text}</p>
                    {c.action === 'natal' && (
                      <Button type="button" onClick={scrollToNatalForm}
                        className={`mt-3 ${CTA_BTN_CLASS}`} data-testid="why-card-natal-cta">
                        {c.actionLabel}
                      </Button>
                    )}
                    {c.link && (
                      <Link to={c.link} className="mt-2 inline-block text-sm font-semibold text-accent-cta-600 underline underline-offset-2"
                        data-testid={`why-card-${c.key}-link`}>
                        {c.linkLabel}
                      </Link>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// Backward-compat re-exports
export function RegisterBenefitsBlock() { return null; }
export function WhyAstrofeelBlock() { return null; }

// ═══════════════════════════════════════════════════════════════════════
//  Accordion Sections Block — 3 раскрывающихся раздела в стиле Hero-кнопок,
//  но фирменного цвета «РАССЧИТАТЬ» (accent-cta-500). Внутри: FAQ, Отзывы,
//  Карта сайта. Все три свёрнуты по умолчанию.
// ═══════════════════════════════════════════════════════════════════════
export function AccordionSectionsBlock() {
  const [openKey, setOpenKey] = useState(null);
  const [faqItems, setFaqItems] = useState(content.faq);
  const [reviews, setReviews] = useState(content.reviews);

  // Все три секции свёрнуты при загрузке, а на экране до раскрытия и так стоит
  // статический фолбэк из homepageContent.json. Поэтому за свежими текстами
  // идём только когда гость раскрыл секцию: холодный старт больше не тратит
  // канал и соединение на данные, которых никто не видит.
  const [faqRequested, setFaqRequested] = useState(false);
  const [reviewsRequested, setReviewsRequested] = useState(false);

  const toggleSection = (key) => {
    setOpenKey((prev) => (prev === key ? null : key));
    if (key === 'faq') setFaqRequested(true);
    if (key === 'reviews') setReviewsRequested(true);
  };

  // FAQ из API
  useEffect(() => {
    if (!faqRequested) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/faq/public`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && Array.isArray(data) && data.length) {
          setFaqItems(data.map((it) => ({ q: it.question, a: it.answer })));
        }
      } catch (_) { /* fallback */ }
    })();
    return () => { cancelled = true; };
  }, [faqRequested]);

  // Отзывы из API
  useEffect(() => {
    if (!reviewsRequested) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews/public`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && Array.isArray(data) && data.length) {
          setReviews(data);
        }
      } catch (_) { /* fallback */ }
    })();
    return () => { cancelled = true; };
  }, [reviewsRequested]);

  const sections = [
    { key: 'faq', label: 'Частые вопросы' },
    { key: 'reviews', label: 'Отзывы пользователей' },
    { key: 'sitemap', label: 'Карта сайта' },
  ];

  return (
    <section className="bg-white py-3 md:py-4 px-4" data-testid="accordion-sections-block">
      <div className="max-w-5xl mx-auto">
        {/* 3 компактные кнопки в одну горизонтальную линию */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {sections.map(({ key, label }) => {
            const isOpen = openKey === key;
            return (
              <button
                key={key}
                onClick={() => toggleSection(key)}
                data-testid={`accordion-btn-${key}`}
                className={`flex items-center justify-between gap-2 rounded-xl px-4 h-11 text-left transition-all shadow-sm
                  ${isOpen
                    ? 'bg-accent-cta-600 hover:bg-accent-cta-600 shadow-md'
                    : 'bg-accent-cta-500 hover:bg-accent-cta-600 hover:shadow-md'}`}
              >
                <span className="text-sm font-bold text-white truncate">{label}</span>
                <ChevronDown className={`w-5 h-5 flex-shrink-0 text-white transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>
            );
          })}
        </div>

        {/* Раскрывающийся контент под линией кнопок */}
        {openKey === 'faq' && (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3" data-testid="accordion-panel-faq">
            {(() => {
              const half = Math.ceil(Math.min(faqItems.length, 6) / 2);
              const visible = faqItems.slice(0, 6);
              return (
                <>
                  <div className="space-y-1.5">
                    {visible.slice(0, half).map((it, i) => <FAQItem key={i} q={it.q} a={it.a} />)}
                  </div>
                  <div className="space-y-1.5">
                    {visible.slice(half).map((it, i) => <FAQItem key={i} q={it.q} a={it.a} />)}
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {openKey === 'reviews' && (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-3" data-testid="accordion-panel-reviews">
            {reviews.map((r) => (
              <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm" data-testid={`review-${r.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="font-semibold text-slate-800 text-sm">{r.author}</div>
                  <div className="text-amber-400 text-sm">{'★'.repeat(r.rating || 5)}</div>
                </div>
                <p className="text-xs md:text-sm text-slate-700 leading-snug">{r.text}</p>
              </div>
            ))}
          </div>
        )}

        {openKey === 'sitemap' && (
          <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-1.5" data-testid="accordion-panel-sitemap">
            {content.footerLinks.main.map((l) => (
              <Link
                key={l.path}
                to={l.path}
                className="text-xs md:text-sm text-slate-700 hover:text-accent-cta-600 py-1.5 px-2 rounded hover:bg-slate-50 transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
export function FAQBlock({ faq = null }) {
  const [items, setItems] = useState(faq && faq.length ? faq : content.faq);

  useEffect(() => {
    if (faq && faq.length) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/faq/public`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && Array.isArray(data) && data.length) {
          setItems(data.map((it) => ({ q: it.question, a: it.answer })));
        }
      } catch (_) {
        // fallback to JSON content
      }
    })();
    return () => { cancelled = true; };
  }, [faq]);

  const visible = items.slice(0, 6);
  const half = Math.ceil(visible.length / 2);
  const left = visible.slice(0, half);
  const right = visible.slice(half);

  return (
    <section className="bg-white py-2 md:py-2.5 px-4" data-testid="faq-block">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-sm md:text-base font-heading font-bold text-center text-slate-900 mb-2">
          Частые вопросы
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3">
          <div className="space-y-1.5">
            {left.map((item, i) => <FAQItem key={i} q={item.q} a={item.a} />)}
          </div>
          <div className="space-y-1.5">
            {right.map((item, i) => <FAQItem key={i} q={item.q} a={item.a} />)}
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  Reviews Block — сиреневый фон, свёрнут по умолчанию
// ═══════════════════════════════════════════════════════════════════════
export function ReviewsBlock() {
  const [open, setOpen] = useState(false);
  return (
    <section className={`${TINT_BG_CLASS} py-2 md:py-2.5 px-4`} data-testid="reviews-block">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-sm md:text-base font-heading font-bold text-slate-900">
            Отзывы пользователей
          </h2>
          <Button
            size="sm"
            variant="outline"
            className="h-8 px-4 text-xs font-medium border-accent-cta-300 text-accent-cta-600 hover:bg-white hover:text-accent-cta-700"
            onClick={() => setOpen((v) => !v)}
            data-testid="reviews-toggle-btn"
          >
            {open ? 'Скрыть отзывы' : 'Показать отзывы'}
            <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${open ? 'rotate-180' : ''}`} />
          </Button>
        </div>
        {open && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2" data-testid="reviews-list">
            {content.reviews.map((r) => (
              <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-2.5" data-testid={`review-${r.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="font-semibold text-slate-800 text-xs md:text-sm">{r.author}</div>
                  <div className="text-amber-400 text-xs">{'★'.repeat(r.rating || 5)}</div>
                </div>
                <p className="text-xs text-slate-700 leading-snug">{r.text}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// Backward-compat
export function ReviewsFAQBlock() { return null; }

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  const isHTML = typeof a === 'string' && /<[a-z][\s\S]*>/i.test(a);
  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between gap-3 text-left px-3 py-1.5 hover:bg-slate-50 rounded-xl"
        data-testid="faq-item"
      >
        <span className="text-xs md:text-sm font-medium text-slate-800 flex-1">{q}</span>
        <ChevronDown className={`w-4 h-4 flex-shrink-0 text-slate-500 mt-0.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        isHTML ? (
          <div className="px-3 pb-2 text-xs md:text-sm text-slate-600 leading-snug prose prose-sm max-w-none prose-a:text-accent-cta-600" dangerouslySetInnerHTML={{ __html: a }} />
        ) : (
          <div className="px-3 pb-2 text-xs md:text-sm text-slate-600 leading-snug">{a}</div>
        )
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  Sitemap collapsible
// ═══════════════════════════════════════════════════════════════════════
export function SitemapBlock() {
  const [open, setOpen] = useState(false);
  return (
    <section className="bg-white py-6 px-4 border-t border-slate-100" data-testid="sitemap-block">
      <div className="max-w-5xl mx-auto">
        <button
          onClick={() => setOpen(!open)}
          className="w-full flex items-center justify-between text-left px-2 py-2 text-slate-800 hover:text-accent-cta-600"
        >
          <span className="text-sm md:text-base font-semibold">Карта сайта</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 pt-3">
            {content.footerLinks.main.map((l) => (
              <Link key={l.path} to={l.path} className="text-sm text-slate-600 hover:text-accent-cta-600 py-1">
                {l.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  Footer (общий подвал сайта)
// ═══════════════════════════════════════════════════════════════════════
export function SiteFooter() {
  return (
    <footer className={`${TINT_BG_CLASS} py-8 md:py-10 px-4 border-t border-accent-cta-150`} data-testid="site-footer">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4 text-sm mb-3">
          {content.footerLinks.docs.map((d, i) => (
            <React.Fragment key={d.path}>
              {i > 0 && <span className="text-slate-300">•</span>}
              <Link to={d.path} className="text-slate-600 hover:text-accent-cta-600 transition-colors">{d.label}</Link>
            </React.Fragment>
          ))}
          <span className="text-slate-300">•</span>
          <Link to="/lunnyy-kalendar" className="text-slate-600 hover:text-accent-cta-600" data-testid="footer-lunar-link">Лунный календарь</Link>
          <span className="text-slate-300">•</span>
          <Link to="/contacts" className="text-slate-600 hover:text-accent-cta-600">Контакты</Link>
        </div>
        <div className="text-center text-xs text-slate-500">
          © {new Date().getFullYear()} {SITE_NAME}. Филатова С.В., ИНН 502478230160, самозанятая (НПД)
        </div>
        <PaymentSystemsLine className="text-center text-xs text-slate-500 mt-1" />
        <div className="text-center text-xs text-slate-400 mt-2">
          Сервис носит информационный характер и не гарантирует результат.
        </div>
      </div>
    </footer>
  );
}
